import type { ElementType } from "react";
import { Briefcase, Sparkles } from "lucide-react";

// Plan Types - debe coincidir con la base de datos
export type PlanType =
  | "lawyer_free"
  | "lawyer_professional"
  | "lawyer_professional_pro";

export type UserType = "lawyer" | "student" | "client";

export type PlanTier = "free" | "professional" | "pro";

export interface PlanLimits {
  documents_per_month: number | null;
  consultations_per_month: number | null;
  cases_per_month: number | null;
  document_reviews_per_month: number | null;
  case_summaries_per_month: number | null;
  storage_mb: number;
}

export interface PlanConfig {
  name: string;
  nameEs: string;
  price: number;
  period: "month" | "year";
  features: string[];
  featuresEs: string[];
  icon?: ElementType;
  color: string;
  userType: UserType;
  isPopular?: boolean;
  highlightColor?: string;
  tier: PlanTier;
  highlightFeatures: string[];
  limits: PlanLimits;
}

// Configuración centralizada de todos los planes
export const PLANS_CONFIG: Record<PlanType, PlanConfig> = {
  // LAWYER PLANS
  lawyer_free: {
    name: "Lawyer Free",
    nameEs: "Abogado Gratis",
    price: 0,
    period: "month",
    color: "from-tiber-400 to-tiber-600",
    icon: Briefcase,
    userType: "lawyer",
    tier: "free",
    highlightFeatures: [
      "plans.lawyer_free.features.cases",
      "plans.lawyer_free.features.consultations",
      "plans.lawyer_free.features.documents",
    ],
    limits: {
      documents_per_month: 2,
      consultations_per_month: 12,
      cases_per_month: 3,
      document_reviews_per_month: 2,
      case_summaries_per_month: 0,
      storage_mb: 100,
    },
    features: [
      "2 legal documents/month",
      "12 consultations/month",
      "3 cases/month",
      "2 AI document reviews/month",
      "100MB storage",
      "Basic support",
    ],
    featuresEs: [
      "2 documentos legales/mes",
      "12 consultas/mes",
      "3 casos/mes",
      "2 revisiones IA de documentos/mes",
      "100MB almacenamiento",
      "Soporte básico",
    ],
  },
  lawyer_professional: {
    name: "Professional",
    nameEs: "Profesional",
    price: 49,
    period: "month",
    color: "from-tiber-500 to-tiber-700",
    icon: Briefcase,
    userType: "lawyer",
    tier: "professional",
    highlightFeatures: [
      "plans.lawyer_professional.features.highLimits",
      "plans.lawyer_professional.features.aiFeatures",
      "plans.lawyer_professional.features.billing",
    ],
    limits: {
      documents_per_month: null,
      consultations_per_month: null,
      cases_per_month: null,
      document_reviews_per_month: 10,
      case_summaries_per_month: 5,
      storage_mb: 2048,
    },
    features: [
      "Unlimited documents",
      "Unlimited consultations",
      "Unlimited cases",
      "AI document review (10/month)",
      "AI case summaries (5/month)",
      "Automatic contract generation",
      "Public profile",
      "Share profile link",
      "Receive public inquiries",
      "Billing reports",
      "2GB storage",
    ],
    featuresEs: [
      "Documentos ilimitados",
      "Consultas ilimitadas",
      "Casos ilimitados",
      "Revisión de documentos IA (10/mes)",
      "Resúmenes de casos IA (5/mes)",
      "Generación automática de contratos",
      "Perfil público",
      "Compartir perfil",
      "Recibir consultas públicas",
      "Reportes de facturación",
      "2GB almacenamiento",
    ],
  },
  lawyer_professional_pro: {
    name: "Professional Pro",
    nameEs: "Profesional Pro",
    price: 99,
    period: "month",
    color: "from-dorado to-dorado-dark",
    icon: Sparkles,
    userType: "lawyer",
    isPopular: true,
    tier: "pro",
    highlightFeatures: [
      "plans.lawyer_professional_pro.features.unlimited",
      "plans.lawyer_professional_pro.features.allFeatures",
      "plans.lawyer_professional_pro.features.prioritySupport",
    ],
    limits: {
      documents_per_month: null,
      consultations_per_month: null,
      cases_per_month: null,
      document_reviews_per_month: null,
      case_summaries_per_month: null,
      storage_mb: 10240,
    },
    features: [
      "Everything in Professional",
      "Unlimited AI document review",
      "Unlimited AI case summaries",
      "Advanced analytics",
      "Priority in searches",
      "Priority support",
      "10GB storage",
      "Pro badge on profile",
    ],
    featuresEs: [
      "Todo lo del Profesional",
      "Revisión de documentos IA ilimitada",
      "Resúmenes de casos IA ilimitados",
      "Analytics avanzados",
      "Prioridad en búsquedas",
      "Soporte prioritario",
      "10GB almacenamiento",
      "Badge Pro en perfil",
    ],
  },
};

// Helper functions
export const getPlansByUserType = (userType: UserType): PlanType[] => {
  return Object.keys(PLANS_CONFIG).filter(
    (key) => PLANS_CONFIG[key as PlanType].userType === userType,
  ) as PlanType[];
};

export const getPlanConfig = (planType: PlanType): PlanConfig => {
  return PLANS_CONFIG[planType];
};

/** Safe lookup for plan config when planType may come from DB (wider enum). Returns null if not in PLANS_CONFIG. */
export function getPlanConfigSafe(planType: string): PlanConfig | null {
  return planType in PLANS_CONFIG ? PLANS_CONFIG[planType as PlanType] : null;
}

/** Type guard: true if planType is a supported plan in PLANS_CONFIG */
export function isSupportedPlan(planType: string): planType is PlanType {
  return planType in PLANS_CONFIG;
}

export const getPopularPlanForUserType = (
  userType: UserType,
): PlanType | null => {
  const plans = getPlansByUserType(userType);
  const popularPlan = plans.find((plan) => PLANS_CONFIG[plan].isPopular);
  return popularPlan || null;
};

// Plan hierarchy for upgrades
export const PLAN_HIERARCHY: Record<UserType, PlanType[]> = {
  lawyer: ["lawyer_free", "lawyer_professional", "lawyer_professional_pro"],
  student: [],
  client: [],
};

export const getNextPlan = (
  currentPlanType: PlanType | null,
  userType: UserType | null,
): PlanType | null => {
  if (!currentPlanType || !userType) return null;

  const plans = PLAN_HIERARCHY[userType] || [];
  const currentIndex = plans.indexOf(currentPlanType);

  if (currentIndex === -1 || currentIndex === plans.length - 1) {
    return null; // Already on highest plan or plan not found
  }

  return plans[currentIndex + 1];
};

export function formatPrice(price: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(price);
}

export function formatPlanLimit(limit: number | null): string {
  return limit === null ? "Unlimited" : limit.toString();
}

/** Returns plan configs with id for UpgradeModal and similar components */
export function getPlanConfigsForUserType(
  userType: UserType,
): (PlanConfig & { id: PlanType })[] {
  return getPlansByUserType(userType).map((planType) => ({
    id: planType,
    ...getPlanConfig(planType),
  }));
}
